import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Button,
  VStack,
  SimpleGrid,
  Heading,
  Alert,
  AlertIcon,
  Icon,
  Avatar,
  HStack,
  Divider,
  Badge,
} from '@chakra-ui/react';
import { FaUser, FaLock, FaPhone, FaShieldAlt, FaSave, FaStore } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { changePassword, updateProfile } from '../services/authService';
import toast from 'react-hot-toast';
import api from '../utils/api';

function getShopPublicPageUrl(userId) {
  const base = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');
  return `${base}/shop/${userId}`;
}
export default function Profile() {
  const { user, updateUser } = useAuth();
  
  // 🎨 متغیرهای رنگی برای تم مدرن
  const bgPage = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.200");
  const inputBg = useColorModeValue("gray.50", "gray.900");

  const [isLoading, setIsLoading] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  const commonInputProps = {
    bg: inputBg,
    border: "1px solid",
    borderColor: "transparent",
    borderRadius: "xl",
    _focus: { 
      borderColor: "blue.400", 
      boxShadow: "0 0 0 1px #3b82f6", 
      bg: useColorModeValue("white", "gray.800") 
    },
    _hover: { borderColor: useColorModeValue("gray.300", "gray.600") },
    transition: "all 0.2s"
  };

  // فرم مشخصات فردی
  const profileForm = useForm({
    defaultValues: { firstName: '', lastName: '' },
    mode: 'onChange',
  });

  // فرم تغییر رمز عبور
  const passwordForm = useForm({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  const { register: registerProfile, handleSubmit: handleSubmitProfile, setValue } = profileForm;
  const { register: registerPassword, handleSubmit: handleSubmitPassword, watch, reset, formState: { errors: passwordErrors } } = passwordForm;
  const newPassword = watch('newPassword');

  useEffect(() => {
    setValue('firstName', user?.firstName ?? '');
    setValue('lastName', user?.lastName ?? '');
  }, [user?.firstName, user?.lastName, setValue]);

  const onSubmitProfile = async (data) => {
    setIsProfileLoading(true);
    try {
      const res = await updateProfile(
        data.firstName?.trim() || '',
        data.lastName?.trim() || ''
      );
      if (res?.data) {
        updateUser(res.data);
        toast.success('مشخصات با موفقیت به‌روزرسانی شد');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'خطا در به‌روزرسانی پروفایل');
    } finally {
      setIsProfileLoading(false);
    }
  };

  const onSubmitPassword = async (data) => {
    setIsLoading(true);
    try {
      await changePassword(data.currentPassword, data.newPassword);
      toast.success('رمز عبور با موفقیت تغییر کرد');
      reset({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'خطا در تغییر رمز عبور');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex direction="column" dir="rtl" bg={bgPage} minH="100vh" pb={10}>
      
      {/* ========================================== */}
      {/* 1. هدر پروفایل (Hero Section) */}
      {/* ========================================== */}
      <Box 
        w="full" 
        bgGradient="linear(to-l, blue.600, purple.600)" 
        pt={{ base: 10, md: 16 }} pb={{ base: 24, md: 32 }} 
        px={{ base: 6, md: 10 }}
        position="relative"
        overflow="hidden"
      >
        <Box position="absolute" top="-10%" left="-5%" w="300px" h="300px" bg="whiteAlpha.100" filter="blur(60px)" rounded="full" />
        
        <Flex maxW="4xl" mx="auto" direction={{ base: "column", md: "row" }} align="center" gap={6} position="relative" zIndex={2}>
          <Avatar 
            size="2xl" 
            name={`${user?.firstName} ${user?.lastName}`} 
            src={user?.avatar} 
            border="4px solid white" 
            boxShadow="xl"
          />
          <VStack align={{ base: "center", md: "start" }} spacing={1} color="white">
            <HStack>
              <Heading size="xl" fontWeight="black" letterSpacing="tight">
                {user?.firstName ? `${user.firstName} ${user.lastName}` : "کاربر کورسل"}
              </Heading>
              <Badge colorScheme="green" variant="solid" borderRadius="full" px={3} fontSize="xs">حساب فعال</Badge>
            </HStack>
            <Text fontSize="lg" opacity={0.9} display="flex" alignItems="center" gap={2}>
              <Icon as={FaPhone} boxSize={3} /> {user?.phone || "بدون شماره"}
            </Text>
            <Text fontSize="sm" opacity={0.7}>مدیریت هوشمند موجودی و فروشگاه‌ها</Text>
            {user?._id && (
              <Button
                mt={3}
                size="sm"
                leftIcon={<FaStore />}
                bg="whiteAlpha.200"
                color="white"
                borderRadius="full"
                _hover={{ bg: 'whiteAlpha.300' }}
                onClick={() => window.open(getShopPublicPageUrl(user._id), '_blank', 'noopener,noreferrer')}
              >
                صفحه عمومی فروشگاه
              </Button>
            )}
          </VStack>
        </Flex>
      </Box>

      {/* ========================================== */}
      {/* 2. محتوای فرم‌ها */}
      {/* ========================================== */}
      <Box maxW="4xl" mx="auto" w="full" px={{ base: 4, md: 6 }} mt="-60px" position="relative" zIndex={3}>
        <SimpleGrid columns={{ base: 1, lg: 1 }} spacing={6}>
          
          {/* کارت اول: مشخصات فردی */}
          <Box bg={cardBg} borderRadius="2xl" p={{ base: 6, md: 8 }} borderWidth="1px" borderColor={borderColor} boxShadow="xl">
            <HStack mb={6}>
              <Flex w="40px" h="40px" borderRadius="xl" bg="blue.50" color="blue.500" align="center" justify="center">
                <Icon as={FaUser} />
              </Flex>
              <Box>
                <Heading size="md" color={textColor}>اطلاعات حساب کاربری</Heading>
                <Text fontSize="xs" color={mutedColor}>نام شما در فاکتورها و پنل نمایش داده می‌شود.</Text>
              </Box>
            </HStack>

            <form onSubmit={handleSubmitProfile(onSubmitProfile)}>
              <VStack spacing={6} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="bold">نام</FormLabel>
                    <Input {...commonInputProps} {...registerProfile('firstName')} placeholder="مثلاً: علی" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="bold">نام خانوادگی</FormLabel>
                    <Input {...commonInputProps} {...registerProfile('lastName')} placeholder="مثلاً: علوی" />
                  </FormControl>
                </SimpleGrid>

                <Alert status="info" variant="subtle" borderRadius="xl" bg="blue.50" _dark={{ bg: "blue.900/20" }}>
                  <AlertIcon color="blue.500" />
                  <Box>
                    <Text fontWeight="bold" fontSize="sm" color="blue.800" _dark={{ color: "blue.200" }}>شماره موبایل (نام کاربری)</Text>
                    <Text fontSize="xs" color="blue.700" _dark={{ color: "blue.300" }}>{user?.phone || "—"} (غیرقابل تغییر از این بخش)</Text>
                  </Box>
                </Alert>

                <Button
                  type="submit"
                  leftIcon={<FaSave />}
                  bgGradient="linear(to-r, blue.500, cyan.400)"
                  color="white"
                  h="50px"
                  borderRadius="xl"
                  fontWeight="bold"
                  isLoading={isProfileLoading}
                  _hover={{ bgGradient: 'linear(to-r, blue.600, cyan.500)', transform: "translateY(-1px)" }}
                >
                  ذخیره تغییرات مشخصات
                </Button>
              </VStack>
            </form>
          </Box>

          {/* کارت دوم: امنیت و رمز عبور */}
          <Box bg={cardBg} borderRadius="2xl" p={{ base: 6, md: 8 }} borderWidth="1px" borderColor={borderColor} boxShadow="xl">
            <HStack mb={6}>
              <Flex w="40px" h="40px" borderRadius="xl" bg="purple.50" color="purple.500" align="center" justify="center">
                <Icon as={FaShieldAlt} />
              </Flex>
              <Box>
                <Heading size="md" color={textColor}>امنیت و رمز عبور</Heading>
                <Text fontSize="xs" color={mutedColor}>برای حفظ امنیت، رمز عبور خود را به صورت دوره‌ای تغییر دهید.</Text>
              </Box>
            </HStack>

            <form onSubmit={handleSubmitPassword(onSubmitPassword)}>
              <VStack spacing={5} align="stretch">
                <FormControl isInvalid={passwordErrors.currentPassword}>
                  <FormLabel fontSize="sm" fontWeight="bold">رمز عبور فعلی</FormLabel>
                  <Input 
                    type="password" 
                    {...commonInputProps} 
                    {...registerPassword('currentPassword', { required: 'وارد کردن رمز فعلی الزامی است' })} 
                    placeholder="••••••••"
                  />
                  <FormErrorMessage>{passwordErrors.currentPassword?.message}</FormErrorMessage>
                </FormControl>

                <Divider borderColor={borderColor} />

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <FormControl isInvalid={passwordErrors.newPassword}>
                    <FormLabel fontSize="sm" fontWeight="bold">رمز عبور جدید</FormLabel>
                    <Input 
                      type="password" 
                      {...commonInputProps} 
                      {...registerPassword('newPassword', { 
                        required: 'رمز عبور جدید الزامی است',
                        minLength: { value: 6, message: 'حداقل ۶ کاراکتر الزامی است' }
                      })} 
                      placeholder="حداقل ۶ کاراکتر"
                    />
                    <FormErrorMessage>{passwordErrors.newPassword?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={passwordErrors.confirmPassword}>
                    <FormLabel fontSize="sm" fontWeight="bold">تکرار رمز عبور جدید</FormLabel>
                    <Input 
                      type="password" 
                      {...commonInputProps} 
                      {...registerPassword('confirmPassword', { 
                        required: 'تکرار رمز الزامی است',
                        validate: (val) => val === newPassword || 'رمز عبور و تکرار آن یکسان نیستند'
                      })} 
                      placeholder="تکرار رمز جدید"
                    />
                    <FormErrorMessage>{passwordErrors.confirmPassword?.message}</FormErrorMessage>
                  </FormControl>
                </SimpleGrid>

                <Button
                  type="submit"
                  leftIcon={<FaLock />}
                  colorScheme="purple"
                  variant="solid"
                  h="50px"
                  borderRadius="xl"
                  fontWeight="bold"
                  isLoading={isLoading}
                  mt={4}
                  _hover={{ transform: "translateY(-1px)", boxShadow: "lg" }}
                >
                  تغییر رمز عبور
                </Button>
              </VStack>
            </form>
          </Box>

        </SimpleGrid>
      </Box>
    </Flex>
  );
}